import { MenuItemType } from '../types/lookups';
import { generateHarvestYearOptions } from '../utils/generateHarvestYearOptions';

export const MAX_CHAR_NUMBER = {
  LARGE: 120,
  MEDIUM: 50,
  SMALL: 16,
  EXTRA_SMALL: 3,
};

export const PAGE_SIZE_OPTIONS = [20, 50, 100];

export const PAGE_SIZE = 20;

export const PAGE = 0;

export const TOASTIFY_AUTO_CLOSE_TIME = 3000;

export const VH_FOR_PLOTS = '60vh';
export const VH_FOR_TABLES = '66vh';
export const TABLE_BOTTOM_PADDING = 0.5;
export const GLOBAL_MAP_LIST_ITEM_SIZE = 240;

export enum ResourceNameEnum {
  PLOT = 'plot',
  COMPANY = 'company',
  USER = 'user',
  LEGALITY = 'legality',
  DDS = 'dds',
}

export enum PermissionTypes {
  FULL = 'full',
  VIEW = 'view',
  NO_ACCESS = 'none',
}

export enum DOCUMENT_TYPE_ENUM {
  LEGALITY_STATEMENT = 1,
  LAND_USE_RIGHTS = 2,
  ENV_PROTECTION = 3,
  THIRD_PARTY_RIGHTS = 4,
  LABOUR_RIGHTS = 5,
  HUMAN_RIGHTS = 6,
  TAX_ANTI_CORRUPTION = 7,
  INDIGENOUS_PEOPLES = 8,
  OTHER = 9,
}

export enum LEGALITY_TYPE_ENUM {
  MY_COMPANY = 0,
  COMPANIES_IN_MY_SUPPLY_CHIAN = 1,
}

export enum DEF_STATUS {
  NOT_REQUESTED = 1,
  PENDING = 2,
  NO = 3,
  YES = 4,
}

export enum LUC_STATUS {
  NOT_REQUESTED = 1,
  PENDING = 2,
  NO = 3,
  YES = 4,
}

export enum FILE_STATUS_ENUM {
  ARCHIVED = 1,
  DELETED = 2,
  ACTIVE = 3,
}

export enum PLOT_STATUS_ENUM {
  ENABLED = 1,
  DELETED = 2,
  PENDING = 3,
  DISPOSED = 4,
}

export enum WMS_LAYERS {
  POLYGONS = 'eudr:data_requested_polygon',
  POINTS = 'eudr:data_requested_point',
  RESPONSE = 'eudr:data_response',
}

export const PERMISSION_IDS = {
  PLOTS: {
    FULL: 1,
    READ: 2,
    NONE: 3,
  },
  COMPANY: {
    FULL: 4,
    READ: 5,
    NONE: 6,
  },
  USER: {
    FULL: 7,
    READ: 8,
    NONE: 9,
  },
  LEGALITY: {
    FULL: 10,
    READ: 11,
    NONE: 12,
  },
};

export const ROLE_IDS = {
  SUPER_SUPER_ADMIN: 1,
  SUPER_ADMIN: 2,
  ADMIN_VIEWER: 3,
  LICENSED_ADMIN: 4,
  LICENSED_MEMBER: 5,
  NON_LICENSED_MEMBER: 6,
};

export const COMPANY_LEVEL_ID = {
  DEV_COMPANY: 1,
  ADMIN_COMPANY: 2,
  LICENSED_COMPANY: 3,
  NON_LICENSED_COMPANY: 4,
  VIRTUAL_COMPANY: 5,
};

export const ACCOUNT_STATUS_ID = {
  PENDING: 1,
  DISABLED: 2,
  ENABLED: 3,
};

export const COMPANY_STATUS_ID = {
  ENABLED: 1,
  DISABLED: 2,
  DELETED: 4,
};

export const API_TAGS = [
  'Auth',
  'Supplier',
  'User',
  'Plots',
  'Plot',
  'Role',
  'Lookup',
  'Cadastral',
  'GeoJson',
  'S3',
  'Counters',
  'Dds',
  'EUDR',
] as const; //without 'as const' ts will no longer recognize api tags and throw error if you use some non existent tag

export const KML_TYPE = 'application/vnd.google-earth.kml+xml';
export const KMZ_TYPE = 'application/vnd.google-earth.kmz';
export const GEOJSON_TYPE = 'application/geo+json';
export const MAX_UPLOAD_SIZE_MB = 28;
export const MAX_UPLOAD_FILE_SIZE = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

export const UPLOAD_DOCUMENTS_VALID_TYPES = [
  'image/bmp',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/geo+json',
  'application/gzip',
  'text/html',
  'application/json',
  'image/jpeg',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/pdf',
  'image/png',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/vnd.adobe.photoshop',
  'application/rtf',
  'image/tiff',
  'text/plain',
  'image/webp',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/xml',
];

export const MAP_BOX_URL_SATELLITE_STREETS =
  'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=';

export const MAP_BOX_ACCESS_TOKEN =
  'pk.eyJ1IjoiYWdzZG9vIiwiYSI6ImNtNXh0bTNrbDA4enQydnI3ZXU1bmk1NnQifQ.4DMAoEVmNUNh96vVbfZ2nQ';

export const HARVEST_YEAR_OPTIONS: MenuItemType[] =
  generateHarvestYearOptions();

export const SHAPE_TYPE = ['shp', 'shx', 'dbf', 'cpg', 'prj', 'qmd'];

export const VALID_EXTENSIONS_FOR_GEO_FILE = [
  '.kml',
  '.kmz',
  '.geojson',
  '.shp',
  '.shx',
  '.dbf',
  '.qmd',
  '.prj',
  '.cpg',
];

export const VALID_EXTENSIONS_FOR_GEOJSON_WITHOUT_DOT = [
  'kml',
  'kmz',
  'geojson',
  'shp',
  'shx',
  'dbf',
  'qmd',
  'prj',
  'cpg',
];

export enum ExportTypeEnum {
  TEMPLATE = 'template',
  GEOJSON = 'geojson',
  EXCEL = 'excel',
}

export const NO_DATA_TEXT = '/';

export const PLOTS_GAP_SMALL = 2;
export const PLOTS_GAP_LARGE = 4;

export const enum SortEnum {
  Company = 1,
  Country,
  FarmerName,
  RahVat,
  CadastralNumber,
  CadastralMunicipality,
  DigiBoundaryRequested,
  OneGeoDataRequested,
  Certificate,
  Area,
  Uid,
  EUDRdeforestation,
  Crop,
  LegalityConfirmed,
  Region,
  PoliticalMunicipality,
  ExternalID,
  DigiDeforestationRequested,
  AreaUnderDeforestationHa,
  LUC,
  LUCDetectionDate,
  LandUseArea,
  LandUseConfidence,
  LandUseChange,
  SoyDetected,
  SourcePolygonId,
  Added,
}

export const DEFAULT_ZOOM_LEVEL = 13;

export const uploadHeight = 210;

export const CADASTRAL_BOX_COLOR = '#DEE2E7';
export const POLYGON_CENTROID_BOX_COLOR = '#C2D0E3';
export const MULTYPOLYGON_BOX_COLOR = '#D4E6F5';

export enum DDS_STATUS_ENUM {
  DRAFT = 1,
  SUBMITTED,
  AVAILABLE,
  ARCHIVED,
  REJECTED,
  DELETED,
  CANCELLED,
  WITHDRAWN,
  // EUDR API V3 (backend DdsStatus 9-12).
  GROUPED,
  OBSOLETE,
  SUSPENDED,
  UPDATED,
}

export enum DdsStatusEnum {
  DRAFT = 'DRAFT',
  CANCELLED = 'CANCELLED',
  WITHDRAWN = 'WITHDRAWN',
  SUBMITTED = 'SUBMITTED',
  REJECTED = 'REJECTED',
  AVAILABLE = 'AVAILABLE',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
  GROUPED = 'GROUPED',
  OBSOLETE = 'OBSOLETE',
  SUSPENDED = 'SUSPENDED',
  UPDATED = 'UPDATED',
}

export enum DdsActivityEnum {
  IMPORT = 'IMPORT',
  EXPORT = 'EXPORT',
  TRADE = 'TRADE',
  DOMESTIC = 'DOMESTIC',
}

// Result of the last EUDR check of a supplier reference (backend DdsReferenceCheckStatus).
export enum DdsReferenceCheckStatusEnum {
  NOT_CHECKED = 0,
  VALID = 1,
  NOT_AVAILABLE = 2,
}

export const enum SortEnumDds {
  ReferenceNumber = 1,
  InternalReferenceNumber,
  Activity,
  Commodity,
  SubmissionDate,
  Status,
  HasReferenced,
}

export const enum DdsUserTypeEnum {
  OPERATOR = 1,
  TRADER,
  OPERATER_REPRESENTATIVE,
  TRADER_REPRESENTATIVE,
}

export const EUDR_ACTIVITY_TYPE = {
  IMPORT: 1,
  EXPORT: 2,
  DOMESTIC: 3,
  TRADE: 4,
};

export const SUPP_UNITS_QUALIFIERS = [
  { code: 'KSD', description: 'kg 90 % sdt' },
  { code: 'MTK', description: 'm2' },
  { code: 'MTQ', description: 'm3' },
  { code: 'MTR', description: 'm' },
  { code: 'NAR', description: 'p/st' },
  { code: 'NPR', description: 'pa' },
  { code: 'DHS', description: 'kg DHS' },
  { code: 'DTN', description: '100 kg' },
  { code: 'DTNM', description: '100 kg/net mas' },
  { code: 'HLT', description: 'hl' },
  { code: 'DTNF', description: '100kg common wheat' },
  { code: 'KAC', description: 'Kg net Ace K' },
  { code: 'KPO', description: 'kg K2O' },
  { code: 'NPR', description: 'pa' },
  { code: 'CCT', description: 'ct/l' },
  { code: 'ASVX', description: '% vol/hl' },
  { code: 'GFI', description: 'gi F/s' },
  { code: 'KNI', description: 'kg N' },
  { code: 'KGMG', description: 'kg/br' },
  { code: 'KNS', description: 'kg N2O2' },
  { code: 'KGMP', description: 'kg/lactic matter' },
  { code: 'MTQC', description: '1000 m3' },
  { code: 'KCC', description: 'kg C5H14CINO' },
  { code: 'KSH', description: 'kg NaOH' },
  { code: 'DTNL', description: '100 kg live weight' },
  { code: 'LTR', description: 'I' },
  { code: 'KGMA', description: 'kg/tot/alc' },
  { code: 'CEN', description: '100 p/st' },
  { code: 'MPR', description: '1000 pa' },
  { code: 'DAP', description: '10 000 kg/polar' },
  { code: 'MIL', description: '100 p/st' },
  { code: 'TJO', description: 'TJ' },
  { code: 'ENP', description: '1 EUR/Nat Curr' },
  { code: 'TNEM', description: '100 kg/net mas' },
  { code: 'TNEE', description: '1000 kg/net eda' },
  { code: 'NAR', description: 'p/st' },
  { code: 'TNEJ', description: '1000 kg/fuel content' },
  { code: 'KCL', description: 'tonne KCI' },
  { code: 'KMA', description: 'kg methylamines' },
  { code: 'TNEK', description: '1000 kg/bioethanol' },
  { code: 'LTRA', description: 'L total alc' },
  { code: 'DTNG', description: '100 kg/br' },
  { code: 'KGMS', description: 'kg/raw sugar' },
  { code: 'KPH', description: 'kg KOH' },
  { code: 'MTQ', description: 'm3' },
  { code: 'NCL', description: 'ce/el' },
  { code: 'KPP', description: 'kg P2O2' },
  { code: 'KLT', description: '1000 I' },
  { code: 'WAT', description: 'Watt' },
  { code: 'ASV', description: '% vol' },
  { code: 'NARB', description: 'b/f' },
  { code: 'DTNE', description: '100 kg/net eda' },
  { code: 'MTK', description: 'm2' },
  { code: 'GRM', description: 'g' },
  { code: 'DTNR', description: '100 kg std qual' },
  { code: 'KGMT', description: 'kg/dry lactic matter' },
  { code: 'KMT', description: 'KM' },
  { code: 'EUR', description: 'EUR' },
  { code: 'TNE', description: '1000 kg' },
  { code: 'DTNZ', description: '100 kg/net/%saccha.' },
  { code: 'KSD', description: 'kg 90 % sdt' },
  { code: 'KUR', description: 'kg U' },
  { code: 'TNEZ', description: '1000 kg/net/% saccha.' },
  { code: 'MWH', description: '1000 kWh' },
  { code: 'CTM', description: 'c/k' },
  { code: 'MTR', description: 'm' },
  { code: 'KGME', description: 'kg/net eda' },
  { code: 'TNER', description: '1000 kg std qual' },
  { code: 'GRT', description: 'GT' },
  { code: 'HMT', description: '100 m' },
  { code: 'KGM', description: 'kg' },
  { code: 'LPA', description: 'I alc. 100%' },
  { code: 'TNEI', description: '1000 kg/biodiesel' },
  { code: 'DTNS', description: '100 kg raw sugar' },
];
